import React from 'react';
import { motion } from 'framer-motion';
import { FiBriefcase, FiMapPin, FiClock, FiCheck } from 'react-icons/fi';
import { personalInfo } from '../data/portfolioData';

const bioLines = personalInfo.bio.match(/.{1,55}(\s|$)/g) || [personalInfo.bio];

const codeLines = [
  '/**',
  ' * About Me',
  ' * --------',
  ...bioLines.map((line) => ` * ${line.trim()}`),
  ' */',
  '',
  'const profile = {',
  `  currentRole: "${personalInfo.title}",`,
  `  experience: "${personalInfo.experience} years",`,
  '  focus: ["Web Apps", "APIs", "Cloud"],',
  `  availableForHire: ${personalInfo.availableForHire}`,
  '};',
];

const syntaxHighlight = (line, index) => {
  if (line.startsWith('/**') || line.startsWith(' *') || line.startsWith(' */')) {
    return <span className="syntax-comment">{line}</span>;
  }
  if (line.startsWith('const ')) {
    return (
      <>
        <span className="syntax-keyword">const</span>{' '}
        <span className="syntax-variable">profile</span>{' '}
        <span>= {'{'}</span>
      </>
    );
  }
  if (line.includes('currentRole:')) {
    return (
      <>
        {'  '}<span className="syntax-variable">currentRole</span>:{' '}
        <span className="syntax-string">"{personalInfo.title}"</span>,
      </>
    );
  }
  if (line.includes('experience:')) {
    return (
      <>
        {'  '}<span className="syntax-variable">experience</span>:{' '}
        <span className="syntax-string">"{personalInfo.experience} years"</span>,
      </>
    );
  }
  if (line.includes('focus:')) {
    return (
      <>
        {'  '}<span className="syntax-variable">focus</span>:{' '}
        [<span className="syntax-string">"Web Apps"</span>,{' '}
        <span className="syntax-string">"APIs"</span>,{' '}
        <span className="syntax-string">"Cloud"</span>],
      </>
    );
  }
  if (line.includes('availableForHire:')) {
    return (
      <>
        {'  '}<span className="syntax-variable">availableForHire</span>:{' '}
        <span className="syntax-keyword">{String(personalInfo.availableForHire)}</span>
      </>
    );
  }
  if (line === '};') {
    return <span>{'};'}</span>;
  }
  return <span>{line}</span>;
};

const infoCards = [
  { icon: <FiBriefcase />, label: 'Experience', value: `${personalInfo.experience} Years` },
  { icon: <FiMapPin />, label: 'Location', value: 'Available Remotely' },
  { icon: <FiClock />, label: 'Status', value: 'Open to Work', hasGreenDot: true },
  { icon: <FiCheck />, label: 'Projects', value: '50+ Delivered' },
];

const About = () => {
  return (
    <section id="about" className="about">
      <motion.h2
        className="section-comment"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        {'// about-me'}
      </motion.h2>

      <div className="about-content">
        <motion.div
          className="code-file"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="file-tab">about-durjoy.js</div>
          <div className="file-content">
            <div className="line-numbers">
              {codeLines.map((_, i) => (
                <span key={i}>{i + 1}</span>
              ))}
            </div>
            <div className="code-text">
              {codeLines.map((line, i) => (
                <div key={i}>{syntaxHighlight(line, i)}</div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="info-cards"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {infoCards.map((card, i) => (
            <motion.div
              className="info-card"
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
            >
              <div className="info-card-icon">{card.icon}</div>
              <div className="info-card-label">{card.label}</div>
              <div className="info-card-value">
                {card.hasGreenDot && <span className="green-dot" />}
                {card.value}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
